import React, {useMemo} from 'react';
import type {SxProps, Theme} from '@mui/material';
import {useTheme} from '@mui/material';
import type {FCWithChildren} from 'types';
import {Text} from 'components';
import {usePreviewConfigurationContext} from '../contexts';

type Props = {
  onClick?: () => void;
};

const getLinkSx = (theme: Theme) => ({
  color: theme.palette.text.primary,
  textDecorationLine: 'underline',
  textUnderlineOffset: '2px',
  textDecorationColor: theme.colors.burgundy,
  cursor: 'pointer',
});

const DefaultTextValue: FCWithChildren<Props> = ({onClick, children}) => {
  const theme = useTheme();
  return onClick ? (
    <Text onClick={onClick} variant="body2" sx={getLinkSx(theme)}>
      {children}
    </Text>
  ) : (
    <Text variant="body1" sx={{color: theme.palette.text.primary}}>
      {children}
    </Text>
  );
};

export const useTextValue = (): FCWithChildren<Props> => {
  const configuration = usePreviewConfigurationContext();
  const theme = useTheme();
  return useMemo(() => {
    const {
      sx,
      variant = 'body1',
      ...textProps
    } = configuration.text?.value ?? {};
    return textProps
      ? ((({onClick, children}) =>
          onClick ? (
            <Text
              onClick={onClick}
              variant={variant}
              {...textProps}
              sx={{...getLinkSx(theme), ...sx}}>
              {children}
            </Text>
          ) : (
            <Text {...textProps} component={'span'} variant={variant} sx={sx}>
              {children}
            </Text>
          )) as FCWithChildren<Props>)
      : DefaultTextValue;
  }, [configuration.text?.value, theme]);
};

export const useStaticTextValue = (
  text: string,
  sx?: SxProps<Theme>,
): React.FC => {
  const configuration = usePreviewConfigurationContext();
  return useMemo(() => {
    const {
      sx: valueSx,
      variant = 'body1',
      ...textProps
    } = configuration.text?.value ?? {};
    const staticSx = (valueSx && sx ? {...valueSx, ...sx} : (sx ?? valueSx)) as
      | SxProps<Theme>
      | undefined;
    return () => (
      <Text variant={variant} {...textProps} sx={staticSx}>
        {text}
      </Text>
    );
  }, [configuration.text?.value, sx, text]);
};
